// frontend/src/utils/fetchWithAuth.js

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
}

/**
 * Fetch with automatic authentication and CSRF protection.
 * 
 * New features:
 * - Detects inactivity timeout and shows friendly message
 * - Handles token auto-refresh transparently (done by backend)
 * - Improved error messages for session expiration
 */
export async function fetchWithAuth(url, options = {}) {
  const csrfToken = getCookie('csrf_token');

  const config = {
    ...options,
    credentials: 'include', // Required for cookies
  };

  // Smart body handling
  if (config.body !== undefined) {
    // Check if it's FormData - don't modify it
    if (config.body instanceof FormData) {
      // Don't set Content-Type - browser will set it with boundary
      config.headers = {
        ...(options.headers || {}),
      };
    }
    // String body - assume JSON
    else if (typeof config.body === 'string') {
      config.headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      };
    }
    // Object body - convert to JSON
    else if (typeof config.body === 'object' && config.body !== null) {
      config.body = JSON.stringify(config.body);
      config.headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      };
    }
  } else {
    config.headers = {
      ...(options.headers || {}),
    };
  }

  // Add CSRF token for state-changing methods
  const method = options.method?.toUpperCase();
  if (csrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }

  const res = await fetch(`${API_BASE_URL}${url}`, config);

  // Handle 401 Unauthorized
  if (res.status === 401) {
    let errorDetail = null;
    let isInactivityTimeout = false;

    // Try to get error details
    try {
      const errorData = await res.json();
      errorDetail = errorData.detail;
      
      // Check if it's an inactivity timeout
      if (errorDetail && (
        errorDetail.toLowerCase().includes('inactivity') ||
        errorDetail.toLowerCase().includes('session expired')
      )) {
        isInactivityTimeout = true;
      }
    } catch (e) {
      // Silent fail - proceed with generic message
    }

    // Show appropriate message
    const toast = getToastFunction();
    if (toast) {
      if (isInactivityTimeout) {
        toast({
          title: 'Session Expired',
          description: 'Your session expired due to inactivity. Please login again.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Authentication Required',
          description: 'Your session has expired. Please login again.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      }
    }

    // Clear storage and redirect
    localStorage.clear();
    sessionStorage.clear();
    
    // Small delay to let user see the toast
    setTimeout(() => {
      window.location.replace('/');
    }, 500);

    throw new Error(errorDetail || 'Unauthorized');
  }

  // Handle 403 Forbidden (CSRF errors)
  if (res.status === 403) {
    try {
      const errorData = await res.json();
      if (errorData.detail && (
        errorData.detail.includes('CSRF') ||
        errorData.detail.includes('csrf')
      )) {
        console.error('CSRF token validation failed - forcing re-login');
        
        const toast = getToastFunction();
        if (toast) {
          toast({
            title: 'Security Error',
            description: 'CSRF validation failed. Please login again.',
            status: 'error',
            duration: 4000,
          });
        }

        localStorage.clear();
        sessionStorage.clear();
        
        setTimeout(() => {
          window.location.replace('/');
        }, 500);

        throw new Error('CSRF validation failed');
      }
    } catch (jsonError) {
      // If we can't parse JSON, just pass through the 403
      if (jsonError.message === 'CSRF validation failed') {
        throw jsonError;
      }
    }
  }

  return res;
}

/**
 * Logout function with proper cleanup.
 * Blacklists token on backend and clears local state.
 */
export async function logout() {
  try {
    const csrfToken = getCookie('csrf_token');

    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: csrfToken ? {
        'X-CSRF-Token': csrfToken
      } : {}
    });
  } catch (error) {
    console.error('Logout failed:', error);
  } finally {
    // Always clear and redirect, even if backend call fails
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace('/');
  }
}

/**
 * Helper to get toast function from Chakra UI context.
 * Works around the fact that we can't use hooks in utility files.
 */
function getToastFunction() {
  // Try to get toast from global context (set in App.js)
  if (window.chakraToast) {
    return window.chakraToast;
  }
  
  // Fallback: log to console if toast unavailable
  return null;
}